import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Map, MapPointProps } from "../framework/map/map";
import LocalShippingIcon from "@mui/icons-material/LocalShippingOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import {
    getWorkTimeTypeColorClass,
    getWorkTimeTypeColorCode,
    getWorkTimeTypeContrastColorClass,
} from "../../models/workShitTimeSlot/workTimeType";
import { SectionHeader } from "../layout/sectionHeader";
import { useTranslation } from "react-i18next";
import MuiMenu from "../framework/muiMenu";
import { MuiSwitch } from "../framework/muiSwitch";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import {
    setGroupMapPoints,
    setSelectedEmployeeId,
    setSelectedWorkShiftTimeSlotId,
    setHoveredWorkShiftTimeSlotId,
} from "../../store/monitorViewSlice";
import { useWorkShiftTimeSlotMapData } from "../../hooks/workTime/useWorkShiftTimeSlotMapData";
import { useVehicleRouteMapData } from "../../hooks/monitorView/useVehicleRouteMapData";
import { useReload } from "../../hooks";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { VehicleData } from "../../models/monitorView/monitorViewTypes";
import { handleVehicleSelect } from "./monitorViewUtils";
import { Base } from "../../framework/base";

interface MonitorViewMapProps {
    loading: boolean;
    data: VehicleData[];
    onRefresh: () => void;
    reloadKey: number;
}

export const MonitorViewMap = ({
    loading,
    data,
    onRefresh,
    reloadKey,
}: MonitorViewMapProps) => {
    const [workShiftPositionsLoading, setWorkShiftPositionsLoading] =
        useState(false);
    const { t } = useTranslation();
    const groupPoints = useAppSelector(
        (state) => state.monitorView.groupMapPoints
    );
    const showNoWorkTime = useAppSelector(
        (state) => state.monitorViewFilters.noWorkTime
    );
    const selectedWorkTimeTypeIds = useAppSelector(
        (state) => state.monitorViewFilters.workTimeTypeIds
    );
    const selectedWorkShiftTimeSlotId = useAppSelector(
        (state) => state.monitorView.selectedWorkShiftTimeSlotId
    );
    const selectedVehicleId = useAppSelector(
        (state) => state.monitorView.selectedVehicleId
    );

    const workShift = useAppSelector(
        (state) => state.monitorView.selectedWorkShift
    );
    const hoveredWorkShiftTimeSlotId = useAppSelector(
        (state) => state.monitorView.hoveredWorkShiftTimeSlotId
    );

    const isWorkShiftView = useAppSelector(
        (state) => state.monitorView.isWorkShiftView
    );
    const { isVehicleRouteView, isVehicleDetailView, isVehicleShiftTabView } =
        useAppSelector((state) => state.monitorView);
    const vehicleRoute = useAppSelector(
        (state) => state.monitorView.vehicleRoute
    );
    const selectedVehicleShift = useAppSelector(
        (state) => state.monitorView.selectedVehicleShift
    );
    const vehicleRouteLoading = useAppSelector(
        (state) => state.monitorView.loading
    );
    const dispatch = useAppDispatch();
    const [zoomKey, zoom] = useReload();
    const shouldZoom = useRef(false);

    const {
        routes: workshiftTimeSlotRoutes,
        points: workshiftPoints,
        reload,
        loading: mapDataLoading,
    } = useWorkShiftTimeSlotMapData(
        workShift?.workShiftTimeSlots ?? [],
        selectedWorkShiftTimeSlotId,
        hoveredWorkShiftTimeSlotId
    );

    const { routes: vehicleRoutes, points: vehiclePoints } =
        useVehicleRouteMapData(
            vehicleRoute,
            isVehicleShiftTabView
                ? selectedVehicleShift?.workShiftTimeSlots
                : undefined,
            isVehicleShiftTabView ? selectedWorkShiftTimeSlotId : undefined,
            isVehicleShiftTabView ? hoveredWorkShiftTimeSlotId : undefined
        );

    useEffect(() => {
        reload();
    }, [reloadKey]);

    useEffect(() => {
        if (!!workShift?.workShiftId) {
            setWorkShiftPositionsLoading(true);
            shouldZoom.current = true;
        }
    }, [workShift?.workShiftId]);

    useEffect(() => {
        if (!!selectedWorkShiftTimeSlotId) {
            zoom();
        }
    }, [selectedWorkShiftTimeSlotId]);

    useEffect(() => {
        if (!workShift) {
            zoom();
        }
    }, [workShift]);

    useEffect(() => {
        // Zoom when workShift has changed and routes and points have been loaded
        if (
            shouldZoom.current &&
            workshiftTimeSlotRoutes?.length > 0 &&
            workshiftPoints?.length > 0
        ) {
            setWorkShiftPositionsLoading(false);
            zoom();
            shouldZoom.current = false;
        }
    }, [workshiftTimeSlotRoutes, workshiftPoints]);

    useEffect(() => {
        // Zoom when filters have changed
        if (shouldZoom.current && !!selectedWorkTimeTypeIds) {
            zoom();
            shouldZoom.current = false;
        }
    }, [selectedWorkTimeTypeIds, showNoWorkTime]);

    useEffect(() => {
        // Zoom when selected vehicle changes
        zoom();
    }, [selectedVehicleId]);

    useEffect(() => {
        // Re-center map when filtered data changes (e.g. vehicle status/group filters)
        if (!isVehicleDetailView && !isWorkShiftView) {
            zoom();
        }
    }, [data?.length]);

    useEffect(() => {
        setWorkShiftPositionsLoading(mapDataLoading);
    }, [mapDataLoading]);

    useEffect(() => {
        if (isVehicleShiftTabView || isVehicleRouteView) {
            setWorkShiftPositionsLoading(vehicleRouteLoading);
        }
    }, [vehicleRouteLoading, isVehicleShiftTabView, isVehicleRouteView]);

    useEffect(() => {
        // Zoom when vehicleRoute changes
        if (vehicleRoute?.length > 0) {
            zoom();
        }
    }, [
        vehicleRoute,
        isVehicleRouteView,
        isVehicleDetailView,
        isVehicleShiftTabView,
    ]);

    useEffect(() => {
        // Zoom when vehicleRoute changes
        if (!!isWorkShiftView) {
            zoom();
        }
    }, [isWorkShiftView]);

    const mapPoints = useMemo(
        () =>
            data
                ?.filter(
                    (d) =>
                        // Filter by selected vehicle, if present
                        !selectedVehicleId || selectedVehicleId === d.vehicleId
                )
                .map(
                    (d) =>
                        ({
                            id: d.vehicleId,
                            coords: d.coords,
                            icon: (
                                <LocalShippingIcon
                                    className={`text-${getWorkTimeTypeContrastColorClass(
                                        d.workTimeTypeType
                                    )}`}
                                />
                            ),
                            text: d.registerNumber,
                            fullText: [
                                d.registerNumber,
                                d.employeeName,
                                d.workTimeTypeName,
                                d.driverGmt
                                    ? `${t("monitorView.tachograph.driverCardInserted")} ${Base.dateStrToOriginalTimezoneTimeStr(d.driverGmt)}`
                                    : null,
                            ]
                                .filter(Boolean)
                                .join(" - "),
                            textColorClass: getWorkTimeTypeContrastColorClass(
                                d.workTimeTypeType
                            ),
                            textBgColorClass: getWorkTimeTypeColorClass(
                                d.workTimeTypeType
                            ),
                            groupColor: getWorkTimeTypeColorCode(
                                d.workTimeTypeType
                            ),
                            groupName: d.workTimeTypeName,
                        }) as MapPointProps
                ),
        [data, selectedVehicleId]
    );

    return (
        <Box
            display="flex"
            flexDirection="column"
            minHeight="100%"
            sx={{ position: "relative" }}
        >
            {workShiftPositionsLoading && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                    }}
                >
                    <LoadingIndicator loading />
                </Box>
            )}
            <div>
                <SectionHeader>
                    <Typography
                        component="div"
                        sx={{ flexGrow: 1 }}
                        className="section-header text-truncate"
                    >
                        {t("monitor")}
                    </Typography>
                    <IconButton
                        color="inherit"
                        onClick={() => zoom()}
                        title={t("map.centerFocus")}
                    >
                        <CenterFocusStrongIcon />
                    </IconButton>
                    <IconButton
                        color="inherit"
                        onClick={onRefresh}
                        disabled={loading}
                    >
                        <RefreshIcon />
                    </IconButton>
                    <MuiMenu
                        label={<SettingsIcon color="white" />}
                        keepOpenAfterSelect
                        items={[
                            {
                                label: (
                                    <MuiSwitch
                                        label={t("map.groupIcons")}
                                        checked={groupPoints}
                                        readOnly
                                        onChange={(_, checked) =>
                                            dispatch(setGroupMapPoints(checked))
                                        }
                                    />
                                ),
                            },
                        ]}
                    />
                </SectionHeader>
            </div>
            {!!mapPoints ? (
                <Map
                    points={
                        isVehicleShiftTabView || isVehicleRouteView
                            ? vehiclePoints
                            : !!workShift
                              ? workshiftPoints
                              : mapPoints
                    }
                    routes={
                        isVehicleShiftTabView || isVehicleRouteView
                            ? vehicleRoutes
                            : !!workShift
                              ? workshiftTimeSlotRoutes
                              : []
                    }
                    autoZoom={false}
                    zoomKey={zoomKey}
                    groupPoints={groupPoints}
                    pointSelectionEnabled
                    onPointSelect={(id) => {
                        if (isWorkShiftView || isVehicleShiftTabView) {
                            dispatch(setSelectedWorkShiftTimeSlotId(id));
                        } else {
                            handleVehicleSelect(
                                id,
                                data,
                                isVehicleDetailView,
                                dispatch
                            );
                        }
                    }}
                    onRouteSelect={(id) => {
                        if (isWorkShiftView || isVehicleShiftTabView) {
                            dispatch(setSelectedWorkShiftTimeSlotId(id));
                        }
                    }}
                    onPointHover={(id) => {
                        if (isWorkShiftView || isVehicleShiftTabView) {
                            dispatch(setHoveredWorkShiftTimeSlotId(id));
                        }
                    }}
                    onRouteHover={(id) => {
                        if (isWorkShiftView || isVehicleShiftTabView) {
                            dispatch(setHoveredWorkShiftTimeSlotId(id));
                        }
                    }}
                    hoverMenuEnabled
                />
            ) : null}
        </Box>
    );
};
